import React, { useMemo } from 'react';
import { Product } from 'schema-dts';

import { useDeps } from 'src/hooks/useDeps';
import { BaseProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { getOrganizationSchema } from 'src/components/Schema/Organization';

import { Schema } from '../Schema/Schema';
import { MediaEntry, ProductPrice } from '../../types/global-types';

type ProductSchemaProps = {
    price: ProductPrice;
    product: BaseProduct;
};

export const ProductSchema = React.memo(function ProductSchema(props: ProductSchemaProps) {
    const { product, price } = props;
    const { productSchemaAvailability } = product.attrs ?? {};
    const {
        env: { DOMAIN },
        contact,
        socialLinks,
    } = useDeps();

    const productSchema = useMemo(() => {
        let availability;

        if (productSchemaAvailability) {
            availability = `http://schema.org/${productSchemaAvailability}`;
        }

        const schema = {
            ...getImages(product.media_entries),
            '@context': 'http://schema.org',
            '@type': 'Product',
            description: product.description,
            name: product.name,
            offers: {
                '@type': 'Offer',
                availability,
                price: price.maximum_price,
                priceCurrency: 'EUR',
                seller: getOrganizationSchema(contact, socialLinks),
                url: `https://${DOMAIN}/${product.url_key}`,
            },
            sku: product.sku,
            url: `https://${DOMAIN}/${product.url_key}`,
        } as Product;

        return schema;
    }, [
        DOMAIN,
        contact,
        price.maximum_price,
        product.description,
        product.media_entries,
        product.name,
        product.sku,
        product.url_key,
        productSchemaAvailability,
        socialLinks,
    ]);

    return <Schema input={productSchema} />;
});

ProductSchema.displayName = 'ProductSchema';

function getImages(mediaEntries: MediaEntry[]): { image: string[] } | undefined {
    if (!mediaEntries.length) return undefined;
    return {
        image: mediaEntries.map((image) => `${image.medium_url}`),
    };
}
